import type { MealsConfigurationDocType, MealType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import {
  defineRxdbCollectionModel,
  defineRxdbMigration,
  defineRxdbMigrations,
  toRxdbMigrationStrategies,
} from './migration';

export const MEALS_COLLECTION = 'meals' as const;
export const MEALS_SCHEMA_VERSION = 1;

type MealsConfigurationDocTypeV0 = Omit<MealsConfigurationDocType, 'meals'> & {
  meals: Array<Omit<MealsConfigurationDocType['meals'][number], 'mealType'>>;
};

export const mealsSchemaLiteral = {
  title: 'meals schema',
  version: MEALS_SCHEMA_VERSION,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 200 },
    userId: { type: 'string', maxLength: 100 },
    meals: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'number', minimum: 0, maximum: 9, multipleOf: 1 },
          displayOrder: { type: 'number' },
          title: { type: 'string' },
          enabled: { type: 'boolean' },
          mealType: { type: 'string', enum: ['meal', 'snack'] },
          startTime: { type: 'string' },
          endTime: { type: 'string' },
        },
        required: ['id', 'displayOrder', 'title', 'enabled', 'mealType'],
      },
    },
    createdAt: { type: 'string', maxLength: 30 },
    updatedAt: { type: 'string', maxLength: 30 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'meals', 'createdAt', 'updatedAt', 'deletedAt'],
  indexes: [['userId', 'updatedAt']],
} as const;

export interface MealsCheckpoint {
  updatedAt: string;
  id: string;
}

type _MealsSchemaDocType = Omit<
  ExtractDocumentTypeFromTypedRxJsonSchema<typeof mealsSchemaLiteral>,
  'meals'
> & {
  meals: MealsConfigurationDocType['meals'];
};
type _MealsDocTypeInSync = Assert<AssertEqual<_MealsSchemaDocType, MealsConfigurationDocType>>;

export const inferMealType = (title: string): MealType => {
  return /\bsnack\b/i.test(title) ? 'snack' : 'meal';
};

export const mealsSchema: RxJsonSchema<MealsConfigurationDocType> = mealsSchemaLiteral;
export const mealsMigrationSteps = defineRxdbMigrations(
  defineRxdbMigration<MealsConfigurationDocTypeV0, MealsConfigurationDocType, 1>(
    1,
    (old) => ({
      ...old,
      meals: old.meals.map((meal) => ({
        ...meal,
        mealType: inferMealType(meal.title),
      })),
    }),
  ),
);
export const mealsMigrations = toRxdbMigrationStrategies(mealsMigrationSteps);

export const mealsModel = defineRxdbCollectionModel<typeof MEALS_COLLECTION, MealsConfigurationDocType>({
  collection: MEALS_COLLECTION,
  schema: mealsSchema,
  migrationStrategies: mealsMigrations,
});
